import React, { useEffect, useRef } from 'react';
import MessageBubble from './MessageBubble';
import { THEME } from '../../styles/theme';

// ── Typing indicator ───────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', paddingRight: '40px' }}>
      <div style={{
        width:          '36px',
        height:         '36px',
        borderRadius:   '50%',
        background:     'rgba(0,212,255,0.1)',
        border:         `2px solid rgba(0,212,255,0.3)`,
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        fontSize:       '16px',
        flexShrink:     0,
      }}>
        🤖
      </div>
      <div style={{
        background:   'rgba(13,21,38,0.8)',
        border:       '1px solid rgba(0,212,255,0.1)',
        borderRadius: '4px 16px 16px 16px',
        padding:      '16px 20px',
        display:      'flex',
        alignItems:   'center',
        gap:          '5px',
      }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{
            width:            '7px',
            height:           '7px',
            borderRadius:     '50%',
            background:       THEME.colors.blue,
            display:          'inline-block',
            animation:        `jarvisPulse 1.2s ease-in-out ${i * 0.2}s infinite`,
          }} />
        ))}
        <span style={{ fontSize: '12px', color: THEME.colors.textMuted, marginLeft: '8px', fontFamily: THEME.fonts.hud, letterSpacing: '2px' }}>
          ANALYZING
        </span>
      </div>
    </div>
  );
}

// ── Empty / welcome state ─────────────────────────────────────────────
function WelcomeState() {
  const hour = new Date().toLocaleString('en-IN', { hour: 'numeric', hour12: false, timeZone: 'Asia/Kolkata' });
  const greeting = parseInt(hour) < 12 ? 'Good morning' : parseInt(hour) < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div style={{
      flex:           1,
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      padding:        '40px 20px',
      textAlign:      'center',
    }}>
      <div style={{ fontSize: '64px', marginBottom: '16px' }}>🤖</div>
      <div style={{
        fontSize:      '24px',
        fontWeight:    '700',
        color:         THEME.colors.blue,
        letterSpacing: '3px',
        fontFamily:    THEME.fonts.hud,
        marginBottom:  '8px',
      }}>
        {greeting.toUpperCase()}, SIR
      </div>
      <div style={{ fontSize: '14px', color: THEME.colors.textMuted, maxWidth: '340px', lineHeight: '1.7', marginBottom: '32px' }}>
        JARVIS is online and ready. Ask me about current XAU/USD setups, market structure, or use a quick prompt below.
      </div>
      <div style={{
        display:      'grid',
        gridTemplateColumns: '1fr 1fr',
        gap:          '10px',
        maxWidth:     '360px',
        width:        '100%',
      }}>
        {[
          { icon: '📊', title: 'Today\'s Bias',    desc: 'D1 + H4 direction' },
          { icon: '🎯', title: 'Find Setup',        desc: 'Active A/A+ entries' },
          { icon: '📐', title: 'ICT Analysis',      desc: 'OBs, FVGs, Liquidity' },
          { icon: '🧠', title: 'Knowledge Check',   desc: 'Test JARVIS learning' },
        ].map(card => (
          <div key={card.title} style={{
            background:   'rgba(255,255,255,0.03)',
            border:       '1px solid rgba(0,212,255,0.1)',
            borderRadius: '10px',
            padding:      '14px',
            textAlign:    'left',
          }}>
            <div style={{ fontSize: '20px', marginBottom: '6px' }}>{card.icon}</div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: THEME.colors.text }}>{card.title}</div>
            <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginTop: '2px' }}>{card.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Message list ────────────────────────────────────────────────────────
export default function MessageList({ messages, isThinking }) {
  const bottomRef = useRef(null);

  // Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, isThinking]);

  if (!messages.length && !isThinking) {
    return (
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <style>{`
          @keyframes jarvisPulse {
            0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
            40% { transform: scale(1); opacity: 1; }
          }
        `}</style>
        <WelcomeState />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px 8px' }}>
      <style>{`
        @keyframes jarvisPulse {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      {messages.map(msg => (
        <MessageBubble key={msg.id} message={msg} />
      ))}

      {isThinking && <TypingIndicator />}

      <div ref={bottomRef} />
    </div>
  );
}
